import {useCallback, useState} from 'react'
import {Keyboard, Pressable, View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {useOpenComposer} from '#/lib/hooks/useOpenComposer'
import {
  useCameraPermission,
  usePhotoLibraryPermission,
  useVideoLibraryPermission,
} from '#/lib/hooks/usePermissions'
import {openCamera, openUnifiedPicker} from '#/lib/media/picker'
import {useComposerState} from '#/state/shell/composer'
import {useCurrentAccountProfile} from '#/state/queries/useCurrentAccountProfile'
import {MAX_IMAGES} from '#/view/com/composer/state/composer'
import {ComposerInline} from '#/view/com/feeds/ComposerInline'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {atoms as a, native, useTheme, web} from '#/alf'
import {Button} from '#/components/Button'
import {useSheetWrapper} from '#/components/Dialog/sheet-wrapper'
import {ChevronBottom_Stroke2_Corner0_Rounded as ChevronBottomIcon} from '#/components/icons/Chevron'
import {Camera_Stroke2_Corner0_Rounded as CameraIcon} from '#/components/icons/Camera'
import {Image_Stroke2_Corner0_Rounded as ImageIcon} from '#/components/icons/Image'
import {SubtleHover} from '#/components/SubtleHover'
import {Text} from '#/components/Typography'
import {useAnalytics} from '#/analytics'
import {IS_NATIVE, IS_WEB} from '#/env'

export function ComposerPrompt() {
  const t = useTheme()
  const ax = useAnalytics()
  const {_} = useLingui()
  const {openComposer} = useOpenComposer()
  const composerState = useComposerState()
  const profile = useCurrentAccountProfile()
  const [hover, setHover] = useState(false)
  const {requestCameraAccessIfNeeded} = useCameraPermission()
  const {requestPhotoAccessIfNeeded} = usePhotoLibraryPermission()
  const {requestVideoAccessIfNeeded} = useVideoLibraryPermission()
  const sheetWrapper = useSheetWrapper()

  const onPress = useCallback(() => {
    ax.metric('composerPrompt:press', {})
    if (IS_WEB) {
      openComposer({presentation: 'inline', logContext: 'Fab'})
    } else {
      openComposer({logContext: 'Fab'})
    }
  }, [ax, openComposer])

  const onPressImage = useCallback(async () => {
    ax.metric('composerPrompt:gallery:press', {})

    // On web, inline composer + gallery (same full UI as modal, embedded in feed)
    if (!IS_NATIVE) {
      openComposer({
        presentation: 'inline',
        openGallery: true,
        logContext: 'Fab',
      })
      return
    }

    try {
      const [photoAccess, videoAccess] = await Promise.all([
        requestPhotoAccessIfNeeded(),
        requestVideoAccessIfNeeded(),
      ])

      if (!photoAccess && !videoAccess) {
        return
      }

      if (Keyboard.isVisible()) {
        Keyboard.dismiss()
      }

      const selectionCountRemaining = MAX_IMAGES
      const {assets, canceled} = await sheetWrapper(
        openUnifiedPicker({selectionCountRemaining}),
      )

      if (canceled) {
        return
      }

      if (assets.length > 0) {
        const imageUris = assets
          .filter(asset => asset.mimeType?.startsWith('image/'))
          .slice(0, MAX_IMAGES)
          .map(asset => ({
            uri: asset.uri,
            width: asset.width,
            height: asset.height,
          }))

        if (imageUris.length > 0) {
          openComposer({imageUris, logContext: 'Fab'})
        }
      }
    } catch (err: any) {
      if (!String(err).toLowerCase().includes('cancel')) {
        ax.logger.error('Error opening image picker', {error: err})
      }
    }
  }, [
    ax,
    openComposer,
    requestPhotoAccessIfNeeded,
    requestVideoAccessIfNeeded,
    sheetWrapper,
  ])

  const onPressCamera = useCallback(async () => {
    ax.metric('composerPrompt:camera:press', {})

    try {
      if (!(await requestCameraAccessIfNeeded())) {
        return
      }

      if (IS_NATIVE && Keyboard.isVisible()) {
        Keyboard.dismiss()
      }

      const image = await openCamera({
        mediaTypes: 'images',
      })

      const imageUris = [
        {
          uri: image.path,
          width: image.width,
          height: image.height,
        },
      ]

      openComposer({
        imageUris: IS_NATIVE ? imageUris : undefined,
        logContext: 'Fab',
      })
    } catch (err: any) {
      if (!String(err).toLowerCase().includes('cancel')) {
        ax.logger.error('Error opening camera', {error: err})
      }
    }
  }, [ax, openComposer, requestCameraAccessIfNeeded])

  if (!profile) {
    return null
  }

  if (IS_WEB && composerState?.presentation === 'inline') {
    return <ComposerInline state={composerState} />
  }

  const tweetBoxBorder = t.palette.contrast_200

  return (
    <View
      style={[
        a.w_full,
        a.overflow_hidden,
        {
          borderBottomWidth: 2,
          borderBottomColor: tweetBoxBorder,
        },
        native({marginBottom: 2}),
      ]}>
      <Pressable
        onPress={onPress}
        android_ripple={null}
        accessibilityRole="button"
        accessibilityLabel={_(msg`Compose new post`)}
        accessibilityHint={_(msg`Opens the post composer`)}
        onPointerEnter={() => setHover(true)}
        onPointerLeave={() => setHover(false)}
        style={({pressed}) => [
          a.relative,
          a.flex_row,
          a.align_start,
          {
            paddingLeft: 16,
            paddingRight: 16,
            paddingTop: 12,
            paddingBottom: 12,
            gap: 12,
          },
          web({
            cursor: 'pointer',
            outline: 'none',
          }),
          web(t.atoms.bg_contrast_25),
          pressed && web({outline: 'none'}),
        ]}>
        <SubtleHover hover={hover} />
        <UserAvatar
          avatar={profile.avatar}
          size={40}
          type={profile.associated?.labeler ? 'labeler' : 'user'}
        />
        <View style={[a.flex_1, {gap: 16}]}>
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.rounded_full,
              {
                alignSelf: 'flex-start',
                borderWidth: 1,
                borderColor: tweetBoxBorder,
                paddingLeft: 12,
                paddingRight: 10,
                paddingVertical: 2,
                gap: 4,
              },
            ]}>
            <Text
              style={[
                a.text_sm,
                a.font_bold,
                {color: t.palette.primary_500, includeFontPadding: false},
              ]}>
              <Trans>Everyone</Trans>
            </Text>
            <ChevronBottomIcon
              size="2xs"
              style={{color: t.palette.primary_500}}
            />
          </View>
          <View
            style={[
              a.flex_row,
              a.align_center,
              a.justify_between,
              {minHeight: 28, gap: 12},
            ]}>
            <Text
              style={[
                t.atoms.text_contrast_medium,
                a.text_xl,
                a.flex_1,
                {includeFontPadding: false},
              ]}>
              <Trans>What's happening?</Trans>
            </Text>
            <View style={[a.flex_row, a.gap_md]}>
              {IS_NATIVE && (
                <Button
                  onPress={e => {
                    e.stopPropagation()
                    onPressCamera()
                  }}
                  label={_(msg`Open camera`)}
                  accessibilityHint={_(msg`Opens device camera`)}
                  variant="ghost"
                  shape="round">
                  {({hovered, pressed, focused}) => (
                    <CameraIcon
                      size="lg"
                      style={{
                        color:
                          hovered || pressed || focused
                            ? t.palette.primary_500
                            : t.palette.contrast_400,
                      }}
                    />
                  )}
                </Button>
              )}
              <Button
                onPress={e => {
                  e.stopPropagation()
                  onPressImage()
                }}
                label={_(msg`Add image`)}
                accessibilityHint={_(msg`Opens image picker`)}
                variant="ghost"
                shape="round">
                {({hovered, pressed, focused}) => (
                  <ImageIcon
                    size="lg"
                    style={{
                      color:
                        hovered || pressed || focused
                          ? t.palette.primary_500
                          : t.palette.contrast_400,
                    }}
                  />
                )}
              </Button>
            </View>
          </View>
        </View>
      </Pressable>
    </View>
  )
}
